import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  DialogContent,
  DialogTitle,
  Divider,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from "@mui/joy";
import FactCheckIcon from "@mui/icons-material/FactCheck";

import useQualityCheck from "../../hooks/useQualityCheck";
import useAuthToken from "../../../../utils/useAuthToken";
import QCProgressView from "./QCProgressView";
import QCSectionPanel from "./QCSectionPanel";
import QCSectionErrorNotice from "./QCSectionErrorNotice";
import {
  QCFinding,
  QCSectionResult,
  QCStatus,
  QCStreamCallbacks,
} from "../../types/qcTypes";
import { recoverSectionsFromRun } from "../../utils/qcRunRecovery";

interface QCModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: number;
  documentTitle: string;
  workspaceId: string;
}

const QCModal: React.FC<QCModalProps> = ({
  isOpen,
  onClose,
  documentId,
  documentTitle,
  workspaceId,
}) => {
  const {
    startDocumentQC,
    connectToStream,
    acceptFinding,
    declineFinding,
    revertFinding,
    bulkResolve,
    applyChanges,
    submitFeedback,
    cancel,
  } = useQualityCheck();

  // Auth token for SSE stream (EventSource cannot set headers)
  const getAuthToken = useAuthToken();

  // Modal state
  const [phase, setPhase] = useState<"progress" | "results">("progress");
  const [qcRunId, setQcRunId] = useState<number | null>(null);
  const [qcStatus, setQcStatus] = useState<QCStatus>("pending");
  const [sectionResults, setSectionResults] = useState<
    Map<number, QCSectionResult>
  >(new Map());
  const [totalFindings, setTotalFindings] = useState(0);
  const [totalSections, setTotalSections] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  // Task #491: SSE reconnect state, cleared on success/complete/terminal-error.
  const [reconnect, setReconnect] = useState<{
    attempt: number;
    max: number;
  } | null>(null);

  const completedSections = Array.from(sectionResults.values()).filter(
    (s) => s.status === "complete" || s.status === "error",
  ).length;

  // Start QC automatically when modal opens
  useEffect(() => {
    if (isOpen && qcStatus === "pending") {
      handleStartQC();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleStartQC = async () => {
    try {
      setQcStatus("running");

      const token = await getAuthToken();
      const result = await startDocumentQC(
        documentId,
        workspaceId,
        null,
        "quick_action",
      );
      const runId = result.qc_run_id;
      setQcRunId(runId);

      const callbacks: QCStreamCallbacks = {
        onStarted: (sections: number) => {
          setTotalSections(sections);
          setReconnect(null);
        },
        onSectionStart: (
          sectionIndex: number,
          sectionTitle: string,
          sectionContent: string,
          sectionId?: number | null,
        ) => {
          setSectionResults((prev) => {
            const next = new Map(prev);
            next.set(sectionIndex, {
              sectionIndex,
              sectionId,
              sectionTitle,
              sectionContent,
              status: "checking",
              findings: [],
              findingCount: 0,
            });
            return next;
          });
          setReconnect(null);
        },
        onFinding: (finding: QCFinding) => {
          setSectionResults((prev) => {
            const next = new Map(prev);
            const section = next.get(finding.SectionIndex);
            if (section) {
              next.set(finding.SectionIndex, {
                ...section,
                findings: [...section.findings, finding],
                findingCount: section.findingCount + 1,
              });
            }
            return next;
          });
          setTotalFindings((prev) => prev + 1);
        },
        onSectionComplete: (sectionIndex: number, findingCount: number) => {
          setSectionResults((prev) => {
            const next = new Map(prev);
            const section = next.get(sectionIndex);
            if (section) {
              next.set(sectionIndex, {
                ...section,
                status: "complete",
                findingCount,
              });
            }
            return next;
          });
        },
        onSectionError: (sectionIndex, findingCount, error, section) => {
          setErrorMessage(error);
          setSectionResults((prev) => {
            const next = new Map(prev);
            const existing = next.get(sectionIndex);
            next.set(sectionIndex, {
              sectionIndex,
              sectionId: section?.sectionId ?? existing?.sectionId,
              sectionTitle:
                section?.sectionTitle ||
                existing?.sectionTitle ||
                "Unknown section",
              sectionContent: existing?.sectionContent || "",
              findings: existing?.findings || [],
              findingCount,
              status: "error",
              errorMessage: error,
            });
            return next;
          });
        },
        onComplete: (findings: number, sections: number) => {
          setTotalFindings(findings);
          setTotalSections(sections);
          setQcStatus("completed");
          setPhase("results");
          setReconnect(null);
        },
        onError: (error: string) => {
          // A failed run keeps no results (the server deletes its findings),
          // so nothing that streamed in before the failure stays on screen.
          setErrorMessage(error);
          setSectionResults(new Map());
          setTotalFindings(0);
          setQcStatus("failed");
          setReconnect(null);
        },
        onReconnecting: (attempt: number, max: number) => {
          setReconnect({ attempt, max });
        },
        onRecoveredViaPoll: (run) => {
          // Stream died but BE finished; rebuild section state from the
          // server's findings and each section's recorded outcome, so the
          // results view has authoritative data (findings replace, rather
          // than add to, the ones that had already streamed in).
          setSectionResults((prev) => recoverSectionsFromRun(prev, run));
        },
      };

      connectToStream(runId, token, workspaceId, callbacks);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to start quality check";
      setErrorMessage(message);
      setQcStatus("failed");
    }
  };

  // Finding resolution handlers
  const applyFindingUpdate = (findingId: number, patch: Partial<QCFinding>) => {
    setSectionResults((prev) => {
      const next = new Map(prev);
      for (const [key, section] of next) {
        const updated = section.findings.map((f) =>
          f.FindingID === findingId ? { ...f, ...patch } : f,
        );
        if (updated !== section.findings) {
          next.set(key, { ...section, findings: updated });
        }
      }
      return next;
    });
  };

  const handleAcceptFinding = async (
    findingId: number,
    editedText?: string | null,
  ) => {
    if (!qcRunId) return;
    try {
      await acceptFinding(qcRunId, findingId, workspaceId, editedText);
      applyFindingUpdate(findingId, {
        Resolution: "accepted",
        Helpful: null,
        ...(editedText != null ? { EditedText: editedText } : {}),
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeclineFinding = async (findingId: number) => {
    if (!qcRunId) return;
    try {
      await declineFinding(qcRunId, findingId, workspaceId);
      applyFindingUpdate(findingId, {
        Resolution: "declined",
        Helpful: null,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleRevertFinding = async (findingId: number) => {
    if (!qcRunId) return;
    try {
      await revertFinding(qcRunId, findingId, workspaceId);
      applyFindingUpdate(findingId, {
        Resolution: null,
        ResolvedDate: null,
        Helpful: null,
        EditedText: null,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const applySectionPatch = (
    sectionIndex: number,
    patchFn: (f: QCFinding) => QCFinding,
  ) => {
    setSectionResults((prev) => {
      const next = new Map(prev);
      const section = next.get(sectionIndex);
      if (section) {
        next.set(sectionIndex, {
          ...section,
          findings: section.findings.map(patchFn),
        });
      }
      return next;
    });
  };

  const handleAcceptAllInSection = async (sectionIndex: number) => {
    if (!qcRunId) return;
    try {
      await bulkResolve(qcRunId, "accepted", workspaceId, sectionIndex);
      applySectionPatch(sectionIndex, (f) => ({
        ...f,
        Resolution: "accepted" as const,
        Helpful: null,
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeclineAllInSection = async (sectionIndex: number) => {
    if (!qcRunId) return;
    try {
      await bulkResolve(qcRunId, "declined", workspaceId, sectionIndex);
      applySectionPatch(sectionIndex, (f) => ({
        ...f,
        Resolution: "declined" as const,
        Helpful: null,
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleRevertAllInSection = async (sectionIndex: number) => {
    if (!qcRunId) return;
    try {
      await bulkResolve(qcRunId, null, workspaceId, sectionIndex);
      applySectionPatch(sectionIndex, (f) => ({
        ...f,
        Resolution: null,
        ResolvedDate: null,
        Helpful: null,
        EditedText: null,
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmitFeedback = async (findingId: number, helpful: boolean) => {
    if (!qcRunId) return;
    try {
      await submitFeedback(qcRunId, findingId, helpful, workspaceId);
      applyFindingUpdate(findingId, { Helpful: helpful });
    } catch (err) {
      console.error(err);
    }
  };

  const handleApplyAll = async () => {
    if (!qcRunId || isApplying) return;
    setIsApplying(true);
    try {
      await applyChanges(qcRunId, workspaceId);
      handleClose();
    } catch (err) {
      console.error("Failed to apply changes:", err);
    } finally {
      setIsApplying(false);
    }
  };

  const resetRunState = () => {
    setPhase("progress");
    setQcRunId(null);
    setQcStatus("pending");
    setSectionResults(new Map());
    setTotalFindings(0);
    setTotalSections(0);
    setErrorMessage(null);
    setReconnect(null);
  };

  const handleClose = () => {
    cancel();
    resetRunState();
    onClose();
  };

  // Task #491: restart QC from scratch after a terminal stream failure.
  const handleRetry = () => {
    cancel();
    resetRunState();
    // useEffect fires handleStartQC when qcStatus returns to "pending".
  };

  // Count accepted findings across all sections
  const acceptedCount = Array.from(sectionResults.values()).reduce(
    (count, section) =>
      count +
      section.findings.filter((f) => f.Resolution === "accepted").length,
    0,
  );

  // Sections that have findings (for results display)
  const sectionsWithFindings = Array.from(sectionResults.values()).filter(
    (s) => s.findings.length > 0,
  );
  const sectionsWithErrors = Array.from(sectionResults.values()).filter(
    (s) => s.status === "error",
  );

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <ModalDialog
        sx={{
          maxWidth: 800,
          width: "90vw",
          maxHeight: "85vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ModalClose />
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <FactCheckIcon />
            <Typography>Quality Check: {documentTitle}</Typography>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ overflow: "auto" }}>
          {phase === "progress" ? (
            <QCProgressView
              sections={Array.from(sectionResults.values())}
              totalSections={totalSections}
              completedSections={completedSections}
              totalFindings={totalFindings}
              status={
                qcStatus === "completed"
                  ? "completed"
                  : qcStatus === "failed"
                    ? "failed"
                    : "running"
              }
              errorMessage={errorMessage || undefined}
              reconnectAttempt={reconnect?.attempt}
              reconnectMax={reconnect?.max}
              onRetry={qcStatus === "failed" ? handleRetry : undefined}
            />
          ) : totalFindings === 0 && sectionsWithErrors.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography level="title-md" sx={{ mb: 1 }}>
                No issues found
              </Typography>
              <Typography level="body-sm" color="neutral">
                The quality check completed without finding any issues.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {sectionsWithErrors.length > 0 && (
                <Alert color="warning" variant="soft">
                  <QCSectionErrorNotice sections={sectionsWithErrors} />
                </Alert>
              )}
              {sectionsWithFindings.map((section) => (
                <QCSectionPanel
                  key={section.sectionIndex}
                  sectionTitle={section.sectionTitle}
                  sectionContent={section.sectionContent}
                  findings={section.findings}
                  onAcceptFinding={handleAcceptFinding}
                  onDeclineFinding={handleDeclineFinding}
                  onRevertFinding={handleRevertFinding}
                  onAcceptAll={() =>
                    handleAcceptAllInSection(section.sectionIndex)
                  }
                  onDeclineAll={() =>
                    handleDeclineAllInSection(section.sectionIndex)
                  }
                  onRevertAll={() =>
                    handleRevertAllInSection(section.sectionIndex)
                  }
                  onSubmitFeedback={handleSubmitFeedback}
                />
              ))}
            </Stack>
          )}
        </DialogContent>
        {phase === "results" && (
          <>
            <Divider />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                pt: 1,
              }}
            >
              <Button
                variant="plain"
                color="neutral"
                onClick={handleClose}
                disabled={isApplying}
              >
                Discard
              </Button>
              <Button
                variant="solid"
                color="primary"
                disabled={acceptedCount === 0}
                loading={isApplying}
                onClick={handleApplyAll}
              >
                Apply accepted changes
              </Button>
            </Box>
          </>
        )}
      </ModalDialog>
    </Modal>
  );
};

export default QCModal;
